import { KeyboardArrowDown } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import React from 'react';
import { MyPermitsData, PersonalData } from '../../api/types';
import { Base64Avatar } from '../Base64Avatar/Base64Avatar';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { SituationIndicator } from '../SituationIndicator/SituationIndicator';
import { useTranslations } from '../Translations/TranslationsProvider';

interface MyDataProps {
    personalData: PersonalData;
}

const accordionStyle: React.CSSProperties = {
    boxShadow: '-1px 1px rgba(0, 0, 0, 0.14), 1px 0.5px rgba(0, 0, 0, 0.14)',
};

interface MyDataSectionProps {
    sectionData: MyPermitsData[];
    sectionTitle: string;
}

const MyDataBasicSection: React.FC<MyDataSectionProps> = ({ sectionData, sectionTitle }) => {
    const { translations } = useTranslations();

    return (
        <Box sx={{ my: 2 }}>
            <Accordion disableGutters style={accordionStyle}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown />}
                    sx={{
                        bgcolor: '#e5f6fd',
                    }}
                >
                    <Typography>{sectionTitle}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {sectionData.length === 0 ? (
                        <Typography variant="body2">{translations.MY_DATA_EMPTY_MARKER}</Typography>
                    ) : (
                        sectionData.map((data, index) => (
                            <React.Fragment key={index}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item>
                                                    <Typography variant="body2">{data.description}</Typography>
                                                    <Typography variant="caption">
                                                        {translations.MY_DATA_EXPIRY_LABEL} {data.endDate}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {index !== sectionData.length - 1 && <Divider sx={{ my: 1 }} />}
                            </React.Fragment>
                        ))
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export const MyData: React.FC<MyDataProps> = ({ personalData }) => {
    const { translations } = useTranslations();
    const { userPicture } = useHomeData();

    return (
        <>
            <Base64Avatar src={userPicture} style={{ height: '7rem', width: '7rem' }} />
            <Typography variant="h5" align="center">
                {personalData.fullName}
            </Typography>
            <Typography variant="subtitle1" align="center">
                {personalData.class}
            </Typography>
            <Grid container spacing={1} alignItems="center" justifyContent="center">
                <Grid item>
                    <Typography variant="body2" fontWeight="bold">
                        {translations.MY_DATA_OC2_LABEL}:
                    </Typography>
                </Grid>
                <Grid item>
                    <SituationIndicator
                        negativeStatusText={translations.ACCESS_POINT_STOP}
                        positiveStatusText={translations.ACCESS_POINT_GO}
                        situation={personalData.siteAccess}
                        textOnly
                    />
                </Grid>
            </Grid>
            <Box sx={{ my: 2 }}>
                <Accordion disableGutters defaultExpanded style={accordionStyle}>
                    <AccordionSummary
                        expandIcon={<KeyboardArrowDown />}
                        sx={{
                            bgcolor: '#e5f6fd',
                        }}
                    >
                        <Typography>{translations.MY_DATA_PERSONNEL_TITLE}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Grid item xs={12}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item>
                                            <Typography variant="body2" fontWeight="bold">
                                                {translations.MY_DATA_PERSON_ID_LABEL}:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2">{personalData.personID}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item>
                                            <Typography variant="body2" fontWeight="bold">
                                                {translations.MY_DATA_ORG_UNIT_LABEL}:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2">{personalData.orgUnit}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Box>
            <Box sx={{ my: 2 }}>
                <Accordion disableGutters style={accordionStyle}>
                    <AccordionSummary
                        expandIcon={<KeyboardArrowDown />}
                        sx={{
                            bgcolor: '#e5f6fd',
                        }}
                    >
                        <Typography>{translations.MY_DATA_IDENTIFIERS_TITLE}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={1} sx={{ mb: 1, pt: 1 }}>
                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_CARD_SIT_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.cardSituation}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_CARD_END_DATE_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.cardEndDate}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_ELOCKS_SIT_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.eLocksSituation}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_ELOCKS_END_DATE_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.eLocksEndDate}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_DOSIMETER_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.dosimeterSituation}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ py: '0 !important' }}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Typography variant="body2" fontWeight="bold">
                                            {translations.MY_DATA_BIOMETRICID_LABEL}:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body2">{personalData.biometricIDSituation}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {personalData.vehicles.length > 0 && <Divider sx={{ my: 2 }} />}
                        {personalData.vehicles?.map((vehicle, index) => (
                            <React.Fragment key={index}>
                                <Grid container spacing={2} sx={{ mb: 2, pt: 2 }}>
                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_LICPLATE_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.licensePlate}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_COUNTRY_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.country}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_MANUFACTURER_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.manufacturer}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_MODEL_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.model}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_CAR_NUMBER_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.carNumber ?? '-'}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ py: '0 !important' }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {translations.MY_DATA_CAR_ACCESS_LABEL}:
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">{vehicle.accessToCERN}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {index !== personalData.vehicles.length - 1 && <Divider sx={{ mb: 2 }} />}
                            </React.Fragment>
                        ))}
                    </AccordionDetails>
                </Accordion>
            </Box>
            <MyDataBasicSection sectionData={personalData.permits} sectionTitle={translations.MY_DATA_PERMITS_LABEL} />
            <MyDataBasicSection sectionData={personalData.ranks} sectionTitle={translations.MY_DATA_RANKS_LABEL} />
            <MyDataBasicSection
                sectionData={personalData.requests}
                sectionTitle={translations.MY_DATA_REQUESTS_LABEL}
            />
        </>
    );
};
